import React from "react";
import { FormControlLabel, FormGroup, Checkbox, Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import {
  formatTimeForStorage,
  getTotalMinutes,
  MINUTES_IN_DAY,
  toPickerValue
} from "../lib/hours";

const DAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];

const createEmptyDay = () => ({
  isOpen: false,
  openTime: null,
  closeTime: null
});

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const OpeningHoursForm = ({ openingHours, setOpeningHours }) => {
  const handleCheckboxChange = (day) => (event) => {
    const { checked } = event.target;
    setOpeningHours((prevOpeningHours) => {
      const currentDay = prevOpeningHours[day] || createEmptyDay();
      return {
        ...prevOpeningHours,
        [day]: {
          ...currentDay,
          isOpen: checked,
          ...(checked
            ? {}
            : {
              openTime: null,
              closeTime: null
            })
        }
      };
    });
  };

  const handleTimeChange = (day, timeType) => (time) => {
    setOpeningHours((prevOpeningHours) => {
      const currentDay = prevOpeningHours[day] || createEmptyDay();
      const comparisonValue = timeType === "closeTime" ? currentDay.openTime : null;

      const storedValue = formatTimeForStorage(time, comparisonValue);
      const updatedDay = {
        ...currentDay,
        [timeType]: storedValue
      };

      if (timeType === "openTime" && hasValue(updatedDay.closeTime)) {
        updatedDay.closeTime = formatTimeForStorage(
          updatedDay.closeTime,
          storedValue
        );
      }

      return {
        ...prevOpeningHours,
        [day]: updatedDay
      };
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormGroup>
        {DAYS.map((day) => {
          const dayState = openingHours[day] || createEmptyDay();
          const { isOpen = false, openTime, closeTime } = dayState;

          const closesNextDay =
            isOpen && getTotalMinutes(closeTime) >= MINUTES_IN_DAY;

          return (
            <Grid
              container
              spacing={2}
              key={day}
              style={{ marginBottom: "16px" }}
            >
              <Grid item xs={12} sm={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(isOpen)}
                      onChange={handleCheckboxChange(day)}
                    />
                  }
                  label={
                    day.charAt(0).toUpperCase() + day.slice(1)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TimePicker
                  label="Open Time *"
                  ampm
                  format="hh:mm A"
                  views={["hours", "minutes"]}
                  value={toPickerValue(openTime)}
                  onChange={handleTimeChange(day, "openTime")}
                  disabled={!isOpen}
                  required={isOpen}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TimePicker
                  label="Close Time *"
                  ampm
                  format="hh:mm A"
                  views={["hours", "minutes"]}
                  value={toPickerValue(closeTime)}
                  onChange={handleTimeChange(day, "closeTime")}
                  disabled={!isOpen}
                  required={isOpen}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText:
                        closesNextDay && hasValue(closeTime)
                          ? "Closes next day"
                          : undefined
                    }
                  }}
                />
              </Grid>
            </Grid>
          );
        })}
      </FormGroup>
    </LocalizationProvider>
  );
};

export default OpeningHoursForm;
